import { Stack, Switch, Text, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { IconBell } from '@tabler/icons-react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { toHex } from 'viem'

import l1FeedBanner from '@/assets/l1Feed/l1FeedBanner.jpg'
import RootButton from '@/components/Buttons/RootButton'
import ErrorMessage from '@/components/ErrorMessage'
import Lamina1Logo from '@/components/Lamina1Logo'
import Loading from '@/components/Loading'
import ImageWrapper from '@/components/MediaWrappers/ImageWrapper'
import ModalSkeleton from '@/components/Modals/ModalSkeleton'
import PageSelector from '@/components/PageSelector'
import { SpecialInputText } from '@/components/SpecialInputText'
import useFeed from '@/hooks/useFeed'
import useSessionStorage from '@/hooks/useSessionStorage'
import useSpaceRegistry from '@/hooks/useSpaceRegistry'
import { useAuth } from '@/plugins/auth'
import { Entry, L1_SPACE_FEED_CONTENT_TYPE } from '@/plugins/contentful/api'
import client from '@/plugins/contentful/client'
import { BodyStack } from '@/styled/components'

import Header from '../../Header/Header'
import Content from './Content'
import SpacesInfo from './SpacesInfo'

const Component: React.FC = () => {
  const { address } = useAuth()
  const { id } = useParams()
  const [content, setContent] = useSessionStorage<Entry[]>(`spaceFeed`, [])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorContent, setErrorContent] = useState<string>('')
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
  const [opened, { open, close }] = useDisclosure()
  const [openedErrorJoin, { open: openErrorJoin, close: closeErrorJoin }] = useDisclosure()
  const { loading: loadingSpaces, spacesMetadata } = useSpaceRegistry()

  const spaceId = useMemo(() => Number(id) || 1, [id])
  const spaceIdHex = useMemo(() => toHex(spaceId), [spaceId])

  const {
    joined,
    size,
    members,
    joinSpace,
    leaveSpace,
    loading: loadingFeed,
    error: errorFeed,
  } = useFeed(spaceIdHex)

  const [emailInput, setEmailInput] = useState<string>('')

  const spaceRequiresEmail = useMemo(
    () => spacesMetadata[spaceId]?.metadataInfo?.feedSettings?.emailRequired || false,
    [spacesMetadata]
  )

  const handleEmailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailInput(e.target.value)
  }, [])

  const isEmailValid = useMemo(() => {
    const re = /\S+@\S+\.\S+/
    return re.test(emailInput)
  }, [emailInput])

  const [emailToggle, setEmailToggle] = useState<boolean>(false)

  const handleSetEmailToggle = useCallback(() => {
    setEmailInput('')
    setEmailToggle(prev => !prev)
  }, [])

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Loading
  const loading = useMemo(
    () => loadingFeed || loadingSpaces || isLoading,
    [loadingFeed, loadingSpaces, isLoading]
  )

  // Error handling
  const error = useMemo(() => {
    if (errorFeed.isError) {
      return errorFeed.msg
    }
    return errorContent
  }, [errorFeed, errorContent])

  useEffect(() => {
    if (errorFeed.isError) {
      openErrorJoin()
    }
  }, [errorFeed, openErrorJoin])

  const handleClose = useCallback(() => {
    // Reset always
    setEmailInput('')
    setEmailToggle(false)
    close()
  }, [close])

  const handleJoinSpace = useCallback(() => {
    joinSpace(emailInput)
    handleClose()
  }, [emailInput, joinSpace, handleClose])

  // Get user space status
  const userSpace = useMemo(() => joined.find(({ id: sid }) => sid === String(spaceId)), [joined])

  // Check if user has joined space
  const hasJoined = useMemo(() => !!userSpace, [userSpace])

  // Get the email stored for this space
  const userEmail = useMemo(() => userSpace?.email || '', [userSpace])

  useEffect(() => {
    if (!content.length) {
      setIsLoading(true)
      client
        .getEntries({ content_type: L1_SPACE_FEED_CONTENT_TYPE })
        .then((response: any) => {
          // order responses by timestamp or updatedAt date (if timestamp is not available)
          const sortedResponse = response.items.sort((a: any, b: any) => {
            const aDate = a.fields?.timestamp
              ? new Date(a.fields.timestamp)
              : new Date(a.sys.updatedAt)
            const bDate = b.fields?.timestamp
              ? new Date(b.fields.timestamp)
              : new Date(b.sys.updatedAt)
            return bDate.getTime() - aDate.getTime()
          })
          setContent(sortedResponse)
        })
        .catch((err: any) => {
          setErrorContent(err.message)
          setIsLoading(false)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [content])

  return (
    <Header>
      <BodyStack>
        <Stack
          style={{
            gap: '120px',
            flexDirection: 'row',
            justifyContent: 'left',
          }}
        >
          <Stack style={{ gap: '40px' }}>
            <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              <Lamina1Logo noHeader />
              {!loading && address && (
                <Stack style={{ flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
                  {!hasJoined ? (
                    <RootButton onClick={open}>Join Space</RootButton>
                  ) : (
                    <RootButton secondary disabled>
                      Joined
                    </RootButton>
                  )}
                </Stack>
              )}
            </Stack>
            <ImageWrapper
              src={l1FeedBanner}
              style={{
                height: '180px',
                width: '100%',
                borderRadius: '10px',
                objectFit: 'cover',
                objectPosition: 'left',
              }}
            />
            <PageSelector text="Feed" active onClick={() => {}} style={{ width: 'fit-content' }} />
            <Stack>
              {loading && <Loading />}
              {error && <ErrorMessage message={error} details={error} />}
              {!loading &&
                !errorContent &&
                content &&
                content.length > 0 &&
                content.map((item, index) => (
                  <Content
                    key={item.sys.id}
                    item={item}
                    index={index}
                    hasJoined={hasJoined}
                    handleJoinSpace={handleJoinSpace}
                  />
                ))}
            </Stack>
          </Stack>
          {!isMobile && (
            <SpacesInfo
              members={members}
              nMembers={size}
              nPosts={content.length}
              spaceRequiresEmail={spaceRequiresEmail}
              hasJoined={hasJoined}
              userEmail={userEmail}
              joinSpace={joinSpace}
              leaveSpace={leaveSpace}
            />
          )}
        </Stack>
      </BodyStack>
      <ModalSkeleton opened={opened} title="Join Space" onClose={handleClose}>
        <Stack>
          {spaceRequiresEmail ? (
            <Text>
              Email is required to join in order to receive important updates from the Space owner.
              You can update your email at any time after joining.
            </Text>
          ) : (
            <>
              <Text>
                Your email is optional and will only be used to receive updates from the Space
                owner. You can update or remove your email at any time after joining.
              </Text>
              <Switch
                checked={emailToggle}
                onChange={handleSetEmailToggle}
                label="(Optional) Provide email"
              />
            </>
          )}
          {(spaceRequiresEmail || emailToggle) && (
            <SpecialInputText
              iconLeft={<IconBell size={20} color={theme.colors.l1Primary[0]} />}
              placeholder="Enter your email"
              value={emailInput}
              onChange={handleEmailChange}
            />
          )}
          <Stack style={{ flexDirection: 'row' }}>
            <RootButton style1={false} expand secondary onClick={handleClose}>
              Cancel
            </RootButton>
            <RootButton
              style1={false}
              expand
              onClick={handleJoinSpace}
              disabled={(spaceRequiresEmail || emailToggle) && !isEmailValid}
            >
              Confirm
            </RootButton>
          </Stack>
        </Stack>
      </ModalSkeleton>
      <ModalSkeleton
        opened={openedErrorJoin}
        title={
          errorFeed.call === 'feed.join'
            ? 'Error Joining Space'
            : errorFeed.call === 'feed.leave'
              ? 'Error Leaving Space'
              : 'Error'
        }
        onClose={closeErrorJoin}
      >
        <Stack>
          <ErrorMessage report={false} message={errorFeed.msg || ''} />
          <Stack style={{ flexDirection: 'row' }}>
            <RootButton style1={false} expand onClick={closeErrorJoin}>
              Close
            </RootButton>
          </Stack>
        </Stack>
      </ModalSkeleton>
    </Header>
  )
}
Component.displayName = 'Feed'

// eslint-disable-next-line import/prefer-default-export
export { Component }
